import React from 'react'
import {
  Box,
  Button,
  Card, CardBody, CardHeader,
  HStack, VStack,
  Text,
  Badge,
  Icon,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Heading,
  IconButton,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Progress,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  CircularProgress,
  Center,
  useBreakpointValue
} from '@chakra-ui/react';
import { AddIcon, ViewIcon, CloseIcon, TimeIcon, StarIcon, CheckIcon, HamburgerIcon, SettingsIcon, ChevronRightIcon, SearchIcon, InfoIcon, ExternalLinkIcon } from '@chakra-ui/icons';

// Shared ModernSidebar Component
export const ModernSidebar = ({ state, onRouteChange, getOverview, onRandomChapter, onClose, customNavItems, toggleSidebar }) => {
  const defaultNavItems = [
    {
      icon: ViewIcon,
      label: 'Overview',
      onClick: () => {
        onRouteChange('dashboard')
        onClose?.()
      }
    },
    {
      icon: AddIcon,
      label: 'New Goal',
      onClick: () => {
        onRouteChange('createGoal')
        onClose?.()
      }
    },
    {
      icon: StarIcon,
      label: 'Random Practice',
      onClick: () => {
        onRandomChapter()
        onClose?.()
      }
    }
  ]
  
  const navItems = customNavItems || defaultNavItems

  return (
    <VStack spacing={{ base: 4, md: 6 }} align="stretch" p={{ base: 4, md: 6 }}>
      <Box position="relative">
        <Heading size={{ base: 'md', md: 'lg' }} mb={1} bgGradient="linear(to-br, brand.600, purple.600)" bgClip="text" fontWeight="900">
          Grow
        </Heading>
        <Text color="gray.600" fontSize={{ base: 'xs', md: 'sm' }}>
          Your daily practice companion
        </Text>
        {/* Hamburger toggle button in top right */}
        {toggleSidebar && (
          <IconButton
            icon={<HamburgerIcon />}
            onClick={toggleSidebar}
            position="absolute"
            top={0}
            right={-2}
            size="sm"
            variant="ghost"
            color="brand.600"
            _hover={{
              color: 'brand.700',
              bg: 'brand.50'
            }}
            transition="all 0.3s ease"
            zIndex={10}
          />
        )}
      </Box>

      <VStack spacing={{ base: 1, md: 2 }} align="stretch">
        {navItems.map((item, index) => {
          const getButtonColors = (label) => {
            switch(label) {
              case 'New Goal':
                return {
                  color: 'purple.600',
                  hoverBg: 'purple.50',
                  hoverColor: 'purple.700',
                  activeBg: 'purple.100'
                }
              case 'Random Practice':
                return {
                  color: 'orange.600',
                  hoverBg: 'orange.50',
                  hoverColor: 'orange.700',
                  activeBg: 'orange.100'
                }
              default:
                return {
                  color: 'brand.600',
                  hoverBg: 'brand.50',
                  hoverColor: 'brand.700',
                  activeBg: 'brand.100'
                }
            }
          }

          const colors = getButtonColors(item.label)

          return (
            <Button
              key={index}
              leftIcon={<Icon as={item.icon} boxSize={{ base: 4, md: 5 }} />}
              justifyContent="flex-start"
              variant="ghost"
              color={colors.color}
              _hover={{
                bg: colors.hoverBg,
                transform: { base: 'none', md: 'translateX(4px)' },
                color: colors.hoverColor
              }}
              _active={{ bg: colors.activeBg, transform: 'scale(0.98)' }}
              transition="all 0.2s"
              onClick={item.onClick}
              size={{ base: 'md', md: 'lg' }}
              fontWeight="500"
              borderRadius="lg"
              py={{ base: 5, md: 6 }}
            >
              {item.label}
            </Button>
          )
        })}
      </VStack>
    </VStack>
  )
}

// Shared GoalCard Component
export const GoalCard = ({ goal, onContinueJourney, setLoading, showDeleteButton = false, onDeleteJourney }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  
  const exercises = (goal.exercises || []).filter(ex => ex != null);
  const totalExercises = exercises.length;
  const completedExercises = exercises.filter(ex => ex && ex.completed === true).length;
  const progress = totalExercises > 0 ? Math.floor((completedExercises / totalExercises) * 100) : 0;
  
  // Determine goal state
  const isCompleted = progress === 100;
  const isNotStarted = progress === 0;
  const isInProgress = progress > 0 && progress < 100;
  
  const handleDelete = async () => {
    if (onDeleteJourney) {
      setLoading(true)
      try {
        await onDeleteJourney(goal.id);
        onClose();
      } finally {
        setLoading(false)
      }
    }
  };
  
  const handleContinue = async () => {
    setLoading(true)
    try {
      await onContinueJourney(goal.id)
    } finally {
      setLoading(false)
    }
  }

  // Consistent styling - white background with colored elements only
  const getCardStyle = () => {
    return {
      bg: "white",
      shadow: "md",
      _hover: { shadow: 'lg', transform: 'translateY(-2px)' }
    };
  };

  const getProgressColor = () => {
    if (isCompleted) return "green";
    if (isNotStarted) return "orange";
    return "brand";
  };

  const getButtonStyle = () => {
    if (isCompleted) {
      return {
        colorScheme: "green",
        variant: "outline",
        leftIcon: <ViewIcon />,
        children: "View Goal"
      };
    }
    if (isNotStarted) {
      return {
        colorScheme: "orange",
        variant: "outline",
        children: "Start Goal"
      };
    }
    return {
      colorScheme: "brand",
      variant: "outline",
      children: "Continue Goal"
    };
  };

  return (
    <Card {...getCardStyle()} transition="all 0.2s" position="relative" _active={{ transform: 'scale(0.98)' }} overflow="visible">
      <CardHeader pb={2} px={{ base: 4, md: 5 }} pt={{ base: 3, md: 5 }}>
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={{ base: 1, md: 2 }} flex={1}>
            {/* Status badges above title */}
            <Badge
              colorScheme={getProgressColor()}
              variant="solid"
              fontSize={{ base: '2xs', md: 'xs' }}
              px={{ base: 1.5, md: 2 }}
              py={0.5}
              borderRadius="md"
            >
              <HStack spacing={1}>
                <Icon as={isCompleted ? CheckIcon : isInProgress ? TimeIcon : AddIcon} boxSize={{ base: 2.5, md: 3 }} />
                <Text display={{ base: 'none', sm: 'inline' }}>
                  {isCompleted ? 'Done' : isInProgress ? 'In Progress' : 'New'}
                </Text>
              </HStack>
            </Badge>

            <Heading
              size={{ base: 'xs', md: 'sm' }}
              color="gray.800"
              noOfLines={2}
              wordBreak="break-word"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {goal.name}
            </Heading>
          </VStack>

          {/* Delete button in top right */}
          {showDeleteButton && (
            <Tooltip label="Delete Goal" placement="top">
              <IconButton
                icon={<CloseIcon />}
                size="xs"
                variant="ghost"
                color="red.400"
                _hover={{
                  color: 'red.600',
                  bg: 'red.50'
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  onOpen()
                }}
                zIndex={2}
                ml={1}
              />
            </Tooltip>
          )}
        </HStack>
      </CardHeader>

      <CardBody py={{ base: 2, md: 4 }} px={{ base: 4, md: 5 }}>
        <VStack align="stretch" spacing={{ base: 2, md: 4 }}>
          <Text
            fontSize={{ base: 'xs', md: 'sm' }}
            color="gray.600"
          >
            {completedExercises}/{totalExercises} completed
          </Text>

          <Box>
            <HStack justify="space-between" mb={1}>
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                color="gray.600"
                fontWeight="600"
              >
                Progress
              </Text>
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                color={getProgressColor() + ".600"}
                fontWeight="700"
              >
                {progress}%
              </Text>
            </HStack>
            <Progress
              value={progress}
              size={{ base: 'xs', md: 'sm' }}
              colorScheme={getProgressColor()}
              borderRadius="full"
            />
          </Box>

          <Button
            size={{ base: 'sm', md: 'sm' }}
            onClick={handleContinue}
            {...getButtonStyle()}
            w="full"
          />
        </VStack>
      </CardBody>

      {/* Delete confirmation dialog */}
      {showDeleteButton && (
        <AlertDialog
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent mx={4}>
              <AlertDialogHeader fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold">
                Delete Goal
              </AlertDialogHeader>
              <AlertDialogBody fontSize={{ base: 'sm', md: 'md' }}>
                Are you sure you want to delete <strong>{goal.name}</strong>? This will also delete all exercises.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose} size={{ base: 'sm', md: 'md' }}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleDelete} ml={3} size={{ base: 'sm', md: 'md' }}>
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </Card>
  )
}

// User Profile Modal Component
export const UserProfileModal = ({ isOpen, onClose, username, userStats }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Your Statistics</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Stats */}
            <Box>
              <SimpleGrid columns={2} spacing={4}>
                <Stat>
                  <StatLabel>Practice Streak</StatLabel>
                  <StatNumber color="purple.500">{userStats?.streak || 0}</StatNumber>
                  <StatHelpText>Days in a row</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Completed Exercises</StatLabel>
                  <StatNumber color="green.500">{userStats?.completed_exercises || 0}</StatNumber>
                  <StatHelpText>Total completed</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Active Goals</StatLabel>
                  <StatNumber color="brand.500">{userStats?.goals_count || 0}</StatNumber>
                  <StatHelpText>Current goals</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Completion Rate</StatLabel>
                  <StatNumber color="blue.500">{userStats?.completion_rate || 0}%</StatNumber>
                  <StatHelpText>Overall progress</StatHelpText>
                </Stat>
              </SimpleGrid>
            </Box>

            {/* Actions */}
            <HStack justify="flex-end" spacing={3}>
              <Button colorScheme="brand" onClick={onClose}>
                Close
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Shared UserMenu Component
export const UserMenu = ({ onRouteChange, username, userStats }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <>
      <Menu>
        <MenuButton>
          <Avatar size="sm" name={username} cursor="pointer" />
        </MenuButton>
        <MenuList>
          <MenuItem icon={<InfoIcon />} onClick={onOpen}>
            Statistics
          </MenuItem>
          <MenuItem icon={<SettingsIcon />} onClick={() => onRouteChange('settings')}>
            Account Settings
          </MenuItem>
          <Divider />
          <MenuItem icon={<ExternalLinkIcon />} onClick={() => onRouteChange('login')}>
            Sign Out
          </MenuItem>
        </MenuList>
      </Menu>

      <UserProfileModal
        isOpen={isOpen}
        onClose={onClose}
        username={username}
        userStats={userStats}
      />
    </>
  )
}

// Breadcrumb Navigation Component
export const BreadcrumbNav = ({ route, goalName, exerciseName, goalId, onRouteChange }) => {
  const getBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'Dashboard', onClick: () => onRouteChange('dashboard') }
    ]

    switch (route) {
      case 'dashboard':
        return breadcrumbs

      case 'allGoalsView':
        breadcrumbs.push({ label: 'All Goals', isCurrentPage: true })
        return breadcrumbs

      case 'createGoal':
        breadcrumbs.push({ label: 'Create Goal', isCurrentPage: true })
        return breadcrumbs

      case 'practicesView':
        breadcrumbs.push({ label: 'All Goals', onClick: () => onRouteChange('allGoalsView') })
        breadcrumbs.push({ label: goalName || 'Goal', isCurrentPage: true })
        return breadcrumbs

      case 'practice':
        breadcrumbs.push({ label: 'All Goals', onClick: () => onRouteChange('allGoalsView') })
        if (goalName) {
          breadcrumbs.push({ label: goalName, onClick: () => onRouteChange('practicesView', { goalId: goalId }) })
        }
        breadcrumbs.push({ label: exerciseName || 'Exercise', isCurrentPage: true })
        return breadcrumbs

      default:
        return breadcrumbs
    }
  }

  const breadcrumbs = getBreadcrumbs()

  if (breadcrumbs.length <= 1) {
    return null
  }

  return (
    <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 3, md: 6 }} py={{ base: 1.5, md: 2 }}>
      <HStack spacing={1} align="center" flexWrap="nowrap" fontSize={{ base: 'xs', md: 'sm' }}>
        {breadcrumbs.map((crumb, index) => (
          <HStack key={index} spacing={1} flexShrink={0}>
            {index > 0 && <ChevronRightIcon color="gray.400" boxSize={3} />}
            <Text
              onClick={crumb.onClick}
              color={crumb.isCurrentPage ? 'gray.700' : 'brand.500'}
              fontWeight={crumb.isCurrentPage ? 'medium' : 'normal'}
              cursor={crumb.onClick ? 'pointer' : 'default'}
              _hover={crumb.onClick ? { color: 'brand.600' } : {}}
              whiteSpace="nowrap"
            >
              {crumb.label}
            </Text>
          </HStack>
        ))}
      </HStack>
    </Box>
  )
}

// Enhanced Loading Component
export const LoadingSpinner = ({ size = "md", text = "Loading...", overlay = false }) => {
  const Component = overlay ? 
    ({ children }) => (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0,0,0,0.3)"
        zIndex={9999}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {children}
      </Box>
    ) : 
    ({ children }) => <Center h="200px">{children}</Center>

  return (
    <Component>
      <VStack spacing={4}>
        <CircularProgress 
          isIndeterminate 
          color="brand.500" 
          size={size === "lg" ? "80px" : size === "md" ? "60px" : "40px"}
        />
        <Text color="gray.600" fontSize={size === "lg" ? "lg" : "md"}>
          {text}
        </Text>
      </VStack>
    </Component>
  )
}

// Button with Loading State
export const LoadingButton = ({
  children,
  isLoading = false,
  loadingText = "Loading...",
  onClick,
  ...props
}) => {
  return (
    <Button
      {...props}
      isLoading={isLoading}
      loadingText={loadingText}
      onClick={isLoading ? undefined : onClick}
      disabled={isLoading}
    >
      {children}
    </Button>
  )
}

// Mobile Bottom Navigation Component
export const MobileBottomNav = ({
  currentRoute,
  onRouteChange,
  onRandomChapter,
  onSearchOpen,
  onProfileOpen
}) => {
  const isMobile = useBreakpointValue({ base: true, lg: false })

  if (!isMobile) return null

  const navItems = [
    {
      icon: ViewIcon,
      label: 'Home',
      route: 'dashboard',
      color: 'brand'
    },
    {
      icon: SearchIcon,
      label: 'Search',
      action: onSearchOpen,
      color: 'gray'
    },
    {
      icon: AddIcon,
      label: 'New Goal',
      route: 'createGoal',
      color: 'purple',
      isPrimary: true
    },
    {
      icon: StarIcon,
      label: 'Practice',
      action: onRandomChapter,
      color: 'orange'
    },
    {
      icon: SettingsIcon,
      label: 'Profile',
      action: onProfileOpen,
      color: 'gray'
    }
  ]

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      bg="white"
      borderTop="1px"
      borderColor="gray.200"
      zIndex={1000}
      // Safe area padding for notched devices (iPhone X+)
      pb="env(safe-area-inset-bottom)"
      boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
    >
      <HStack
        justify="space-around"
        align="center"
        py={2}
        px={2}
        maxW="500px"
        mx="auto"
      >
        {navItems.map((item, index) => {
          const isActive = currentRoute === item.route
          const colorScheme = item.color || 'gray'

          const handlePress = () => {
            if (item.action) {
              item.action()
            } else if (item.route) {
              onRouteChange(item.route)
            }
          }

          // Primary action button (center, prominent)
          if (item.isPrimary) {
            return (
              <Box key={index} position="relative" mt={-2}>
                <IconButton
                  icon={<Icon as={item.icon} boxSize={4} />}
                  aria-label={item.label}
                  onClick={handlePress}
                  colorScheme={colorScheme}
                  variant="solid"
                  size="md"
                  borderRadius="full"
                  boxShadow="md"
                  // Touch-friendly: minimum 44px tap target
                  minW="44px"
                  minH="44px"
                  _active={{
                    transform: 'scale(0.95)',
                    boxShadow: 'sm'
                  }}
                />
              </Box>
            )
          }

          return (
            <VStack
              key={index}
              spacing={0.5}
              onClick={handlePress}
              cursor="pointer"
              // Touch-friendly: minimum 44px tap target
              minW="44px"
              minH="44px"
              justify="center"
              align="center"
              py={1}
              px={2}
              borderRadius="lg"
              transition="all 0.2s"
              _active={{
                bg: `${colorScheme}.50`,
                transform: 'scale(0.95)'
              }}
            >
              <Icon
                as={item.icon}
                boxSize={5}
                color={isActive ? `${colorScheme}.500` : 'gray.500'}
                transition="color 0.2s"
              />
              <Text
                fontSize="2xs"
                fontWeight={isActive ? '600' : '500'}
                color={isActive ? `${colorScheme}.600` : 'gray.500'}
                transition="color 0.2s"
              >
                {item.label}
              </Text>
            </VStack>
          )
        })}
      </HStack>
    </Box>
  )
}

// Hook to get bottom padding for content when MobileBottomNav is present
export const useMobileBottomPadding = () => {
  const isMobile = useBreakpointValue({ base: true, lg: false })
  // Bottom nav height (~60px) + safe area
  return isMobile ? 'calc(70px + env(safe-area-inset-bottom))' : '0px'
}